/**
 * Wizard step 7 — Linked Artefacts.
 *
 * Replaces the source app's risk-only picker with Nimbus's standard cross-register
 * linking, which every other register uses. That is a strict superset: it links risks
 * plus controls, issues, incidents, remediations, evidence, documents, assets, people,
 * threats, security objectives, physical sites, legal requirements and interested
 * parties.
 *
 * Was step 6, moved to 7 by product decision — see `AiUseCaseWizard.tsx`'s file header for
 * the full account of what moving a positional step number costs and why it was done anyway.
 * This step being optional made it the cheaper of the two to move: nothing gates on it, and
 * nothing downstream reads a stored "reached step 7" flag — reachability is recomputed live
 * from `canAdvance`, so there is no persisted state this rename could leave inconsistent.
 *
 * Two things worth knowing about this step:
 *
 *  - Links are written through the artefacts API immediately and bidirectionally, not
 *    held in wizard state and saved on Next. So there is nothing to persist here and the
 *    step takes no `update` callback into the draft. The record must already exist, which
 *    it does — the wizard creates the draft before step 1.
 *  - The step remains optional wherever it sits. `canAdvance` returns true for this step
 *    regardless, matching the source — a use case can be submitted with no links — and the
 *    shell carries the `optional: true` flag on STEPS so it is excluded from the progress
 *    denominator and the submission gate wherever it sits, rather than that being inferred
 *    from its position.
 *  - Because nothing here writes to the record, the shell derives this step's rail state from
 *    the artefacts API (the `linkedArtefacts` query) rather than from `linkedRiskIds`, which no
 *    longer has a writer.
 */
import React from 'react';
import { LinkedArtefacts } from '../../../components/LinkedArtefacts';
import type { StepProps } from './types';

const Step7Links: React.FC<StepProps> = ({ useCase }) => {
  return (
    <div>
      <p className="np-sub mb-4">
        <strong>What else does this touch?</strong> Link the risks, controls, issues or
        evidence that relate to this AI use case. Links work in both directions — this use
        case will appear on the linked item too.
      </p>

      {useCase.useCaseId ? (
        <div className="row">
          <LinkedArtefacts
            artefactId={useCase.useCaseId}
            artefactType="ai_use_case"
            artefactRegister=""
          />
        </div>
      ) : (
        <div className="np-banner np-tone-info">
          <i className="ri-information-line"></i>
          Save the use case before linking — links attach to a saved record.
        </div>
      )}

      <p className="np-hint mt-3">
        This step is optional. You can submit the use case for review without any links and
        add them later from the use case page.
      </p>
    </div>
  );
};

export default Step7Links;
